/**
 * Разбор свободных свойств слоя.
 *
 * Свойства приходят из сети как `google.protobuf.Struct` — то есть
 * произвольным объектом. Схема виджета живёт ЗДЕСЬ, а не в протоколе,
 * и это решение из 2.1: жёсткая типизация означала бы правку .proto
 * и перегенерацию у всех на каждое новое свойство виджета.
 *
 * Разбор написан руками, без библиотеки схем. Причина не в
 * принципиальности: полей у трёх примитивов десяток, и зависимость
 * ради них тянула бы за собой обновления, размер бандла (у оверлея
 * он в бюджете) и свою манеру сообщать об ошибках. Когда виджетов
 * станет два десятка — это будет другой разговор, и менять придётся
 * один файл.
 */
import { WidgetPropsError } from './contract.js';
/** Достаёт объект свойств, каким бы он ни пришёл. */
export function asRecord(widget, props) {
    if (props === null || props === undefined) {
        return {};
    }
    if (typeof props !== 'object' || Array.isArray(props)) {
        throw new WidgetPropsError(widget, 'props', 'ожидался объект');
    }
    return props;
}
/** Обязательная непустая строка. */
export function requiredString(widget, props, field) {
    const value = props[field];
    if (typeof value !== 'string') {
        throw new WidgetPropsError(widget, field, 'ожидалась строка');
    }
    if (value.trim() === '') {
        throw new WidgetPropsError(widget, field, 'не может быть пустым');
    }
    return value;
}
/** Необязательная строка из закрытого списка. */
export function oneOf(widget, props, field, allowed, fallback) {
    const value = props[field];
    if (value === undefined || value === null) {
        return fallback;
    }
    if (typeof value !== 'string') {
        throw new WidgetPropsError(widget, field, 'ожидалась строка');
    }
    if (!allowed.includes(value)) {
        throw new WidgetPropsError(widget, field, `допустимо только ${allowed.join(', ')}`);
    }
    return value;
}
/**
 * Число в заданных пределах.
 *
 * NaN и бесконечность отвергаются отдельно от «не число»: они
 * приходят из вычислений на клиенте (деление на ноль при пересчёте
 * масштаба) и, попав в стиль, делают слой неотображаемым — а на вид
 * это обычное число.
 */
export function numberInRange(widget, props, field, min, max, fallback) {
    const value = props[field];
    if (value === undefined || value === null) {
        return fallback;
    }
    if (typeof value !== 'number') {
        throw new WidgetPropsError(widget, field, 'ожидалось число');
    }
    if (!Number.isFinite(value)) {
        throw new WidgetPropsError(widget, field, 'не является числом');
    }
    if (value < min || value > max) {
        throw new WidgetPropsError(widget, field, `допустимо от ${min} до ${max}`);
    }
    return value;
}
/**
 * Цвет в формате #rrggbb.
 *
 * Ровно один формат, всё остальное отвергается. Причина та же, что
 * и в hub: значение уходит прямо в свойство style, а подстановка
 * произвольной строки в CSS означает и внешние запросы (`url(...)`),
 * и дописывание чужих правил.
 */
const HEX_COLOR = /^#[0-9a-fA-F]{6}$/;
export function color(widget, props, field, fallback) {
    const value = props[field];
    if (value === undefined || value === null) {
        return fallback;
    }
    if (typeof value !== 'string' || !HEX_COLOR.test(value)) {
        throw new WidgetPropsError(widget, field, 'цвет обязан быть в формате #rrggbb');
    }
    return value;
}
/**
 * Адрес ресурса.
 *
 * Разрешены только http, https и data — и это не перестраховка.
 * `javascript:` в src исполняется, а оверлей работает без присмотра
 * во время эфира: заметить исполнение чужого кода там будет некому.
 */
const ALLOWED_PROTOCOLS = new Set(['http:', 'https:', 'data:']);
export function url(widget, props, field) {
    const value = requiredString(widget, props, field);
    let parsed;
    try {
        parsed = new URL(value, 'https://overlay.invalid/');
    }
    catch {
        throw new WidgetPropsError(widget, field, 'не разбирается как адрес');
    }
    if (!ALLOWED_PROTOCOLS.has(parsed.protocol)) {
        throw new WidgetPropsError(widget, field, 'допустимы только http, https и data');
    }
    return value;
}
/**
 * Необязательная строка с ограничением длины.
 *
 * Отсутствие и пустая строка — одно и то же: слой, у которого поле
 * не заполнили, и слой, у которого его очистили, должны вести себя
 * одинаково. Различать их значило бы завести два способа сказать
 * «ничего».
 */
export function optionalString(widget, props, field, limit) {
    const value = props[field];
    if (value === undefined || value === null)
        return '';
    if (typeof value !== 'string') {
        throw new WidgetPropsError(widget, field, 'ожидалась строка');
    }
    const trimmed = value.trim();
    if (trimmed.length > limit) {
        throw new WidgetPropsError(widget, field, `не длиннее ${limit} знаков`);
    }
    return trimmed;
}
/** Необязательный адрес: пустой означает «файла нет». */
export function optionalURL(widget, props, field) {
    const value = props[field];
    if (value === undefined || value === null || value === '')
        return '';
    return url(widget, props, field);
}
/**
 * Список чисел — координаты нарисованного.
 *
 * ОТДЕЛЬНЫМ РАЗБОРОМ, А НЕ ЦИКЛОМ ИЗ numberInRange, потому что
 * проверять здесь надо и то, чего у одиночного числа не бывает:
 * длину. Штрих от руки приходит из сети, и список без потолка —
 * это чужой узел, растущий в DOM оверлея, пока хватает памяти.
 *
 * Пары: координаты идут x, y, x, y. Нечётная длина означает
 * потерянную половину точки, и молча отбрасывать её нельзя —
 * рисунок был бы не тем, что нарисовали.
 */
export function pointList(widget, props, field, limit) {
    const value = props[field];
    if (value === undefined || value === null)
        return [];
    if (!Array.isArray(value)) {
        throw new WidgetPropsError(widget, field, 'ожидался список чисел');
    }
    if (value.length % 2 !== 0) {
        throw new WidgetPropsError(widget, field, 'координаты идут парами x, y');
    }
    if (value.length > limit * 2) {
        throw new WidgetPropsError(widget, field, `не длиннее ${limit} точек`);
    }
    for (const item of value) {
        if (typeof item !== 'number' || !Number.isFinite(item)) {
            throw new WidgetPropsError(widget, field, 'ожидались числа');
        }
    }
    return value;
}
/**
 * Свободный текст: то, что человек набирает руками.
 *
 * ОТ optionalString ОТЛИЧАЕТСЯ ТЕМ, ЧТО НЕ ОБРЕЗАЕТ КРАЯ. Пробел
 * в конце — это середина набора: человек написал слово и тянется
 * за следующим. Обрезав его, мы переписали бы узел под курсором
 * и потеряли каретку ровно между двумя словами.
 *
 * Потолок длины при этом обязателен: строка приходит из сети
 * и уходит в DOM оверлея, который работает без присмотра во время
 * эфира.
 */
export function freeText(widget, props, field, limit) {
    const value = props[field];
    if (value === undefined || value === null)
        return '';
    if (typeof value !== 'string') {
        throw new WidgetPropsError(widget, field, 'ожидалась строка');
    }
    if (value.length > limit) {
        throw new WidgetPropsError(widget, field, `не длиннее ${limit} знаков`);
    }
    return value;
}
//# sourceMappingURL=props.js.map